import type { HTMLAttributes, PropsWithChildren } from "react";
import {
  TAspectratioRatio,
  TBadgeProps,
  TImageProps,
  TPillProps,
} from "../../primitives";
import { DefinedDefaults, TWithDataComponent } from "@/ui/lib";

/*-------------------------------------
|  Constants
-------------------------------------*/

export const CARD_LAYOUT_CLASS_MAP = {
  column: "flex-col",
  row: "flex-row [&>*]:w-1/2",
};

export const CARD_ORIENTATION_CLASS_MAP: Record<TCardLayout, Record<TCardOrientation, string>> = {
  column: {
    default: "",
    reverse: "flex-col-reverse",
  },
  row: {
    default: "",
    reverse: "flex-row-reverse",
  },
};

export const CARD_LAYOUT_CONTENT_CLASS_MAP: Record<TCardLayout, Record<TCardOrientation, string>> = {
  column: {
    default: "border-t",
    reverse: "border-b",
  },
  row: {
    default: "border-l",
    reverse: "border-r",
  },
};


/*-------------------------------------
|  Types
-------------------------------------*/

export type TCardLayout = keyof typeof CARD_LAYOUT_CLASS_MAP;
export type TCardOrientation = "default" | "reverse";

export type TCardTags = {
  topLeft?: TPillProps;
  bottomLeft?: TBadgeProps;
  bottomRight?: TPillProps;
};

export type TCardTagsProps = {
  tags?: TCardTags;
};

export type TCardContentProps = PropsWithChildren & {

};

export type TCardImageProps = {
  image: TImageProps;
  ratio?: TAspectratioRatio;
  tags?: TCardTags;
};

export type TCardIntroductionProps = {
  eyebrow?: string;
  title: string;
  description?: string;
};

export type TBaseCardProps = TWithDataComponent & TCardContentProps & TCardImageProps & {
  layout?: TCardLayout;
  orientation?: TCardOrientation;
  className?: string;
};

export type TPreviewCardProps = {
  tags: Pick<TCardTags, "topLeft" | "bottomRight">
} & Pick<TCardIntroductionProps, "title"> & TBaseCardProps & HTMLAttributes<HTMLDivElement>;

export type THorizontalCardProps = {
  tags: Pick<TCardTags, "topLeft">
} & TCardIntroductionProps & TBaseCardProps & HTMLAttributes<HTMLDivElement>;

export type TCardProps = {
} & TCardIntroductionProps & TBaseCardProps & HTMLAttributes<HTMLDivElement>;

export type TBaseCardDefaults = DefinedDefaults<TBaseCardProps, "data-component" | "layout" | "orientation">;
export type TCardImageDefaults = DefinedDefaults<TCardImageProps, "ratio">;

export type THorizontalCardDefaults = DefinedDefaults<THorizontalCardProps, "orientation">;


/*-------------------------------------
|  Defaults
-------------------------------------*/

export const BASE_CARD_DEFAULTS = {
  layout: "column",
  orientation: "default",
  "data-component": "BaseCared",
} satisfies TBaseCardDefaults;

export const CARD_IMAGE_DEFAULTS = {
  ratio: "1:1",
} satisfies TCardImageDefaults;

export const HORIZONTAL_CARD_DEFAULTS = {
  orientation: "default",
} satisfies THorizontalCardDefaults;
